import { apiClient } from "../../../shared";

// Credential flows for the User block.
//
// These duplicate sendEmailCode / verifyEmailCode / postLogout in
// src/services/data_requests.jsx. The originals are left in place because the
// session store still uses them; the copies here exist so this block owns every
// request it makes. See the duplicate ledger in this block's README.

// Endpoint the wallet signature is posted to. The signing itself stays in
// src/services/blockchain/walletService - that is the Wallet block's domain,
// so this block only supplies the URL.
export const WEB3_AUTH_ENDPOINT = "web3_auth";

// Reproduces the error text the pages already surface in toasts.
//
// axios parses a JSON body for us, so the object branch is the normal path. The
// string branch mirrors the old fetch code, which read the body as text and fell
// back to the generic message when it did not parse as JSON.
const messageFrom = (error, fallback) => {
  const data = error?.response?.data;

  if (data && typeof data === "object") return data.detail || fallback;
  if (typeof data === "string" && data) {
    try {
      return JSON.parse(data).detail || fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

/**
 * Email a one-time login code. Also used by "Resend code".
 * @param {string} email
 */
export const requestEmailCode = async (email) => {
  try {
    const { data } = await apiClient.post("user/auth", { email });
    return data;
  } catch (error) {
    throw new Error(
      messageFrom(error, "Failed to send code. Check email and try again."),
    );
  }
};

/**
 * Exchange the one-time code for a session cookie.
 * @param {string} email
 * @param {string} code six digits
 * @returns {Promise<{success?: boolean, message?: string}>}
 */
export const verifyEmailCode = async (email, code) => {
  const query = `user_email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`;

  try {
    const { status, data } = await apiClient.post(`user/check_code?${query}`);

    // 204, or a 200 with an empty body, both mean "verified" with nothing to say.
    if (status === 204 || !data) {
      return { success: true, message: "Login successful!" };
    }
    return typeof data === "string" ? { message: data } : data;
  } catch (error) {
    throw new Error(
      messageFrom(error, "Invalid code or email. Please try again."),
    );
  }
};

/**
 * Trade a Google credential for a session cookie.
 *
 * The credential is forwarded untouched for the backend to verify - it is what
 * mints the cookie. Decoding it client-side instead left the app "logged in"
 * with no session, so every later request failed with "Invalid token".
 *
 * @param {string} credential the JWT from Google Identity Services
 */
export const googleSignIn = async (credential) => {
  const { data } = await apiClient.post("user/google-auth", {
    token: credential,
  });
  return data;
};

/**
 * Invalidate the server session.
 *
 * Never throws - logout must not be blockable by a failing request, since the
 * caller clears local state either way.
 *
 * @returns {Promise<{success: boolean, errorMessage?: string}>}
 */
export const logout = async () => {
  try {
    await apiClient.post("user/logout");
    return { success: true };
  } catch (error) {
    console.error("Error during logout:", error);
    return {
      success: false,
      errorMessage: messageFrom(error, "Failed to log out."),
    };
  }
};

/** Email a password-reset link. */
export const requestPasswordReset = async (email) => {
  const { data } = await apiClient.post("user/password_reset_request", {
    email,
  });
  return data;
};

/** Complete a password reset with the token from the emailed link. */
export const resetPassword = async (token, newPassword) => {
  const { data } = await apiClient.post("user/password_reset", {
    token,
    new_password: newPassword,
  });
  return data;
};
